import { useEffect, useRef, useState } from 'react';
import { Button, Callout, DateField, Modal, NumberField } from '@humanforce/mango-ui-react';
import { DetailPair } from './components/SectionShell';
import { forecastWorkerLeave, type WorkerLeaveAccount, type WorkerLeaveForecast } from './workerDirectoryApi';

const displayHours = (value: number | null) => value === null ? 'Unknown' : value.toFixed(4);
const issueText = (issue: string) => issue === 'unsupported_leave_calculation'
  ? 'This Leave calculation is not supported by this forecast.'
  : 'The selected Leave rule cannot be forecast with the supplied assumptions.';

export function WorkerLeaveForecastDialog({ personId, account, visible, onHide }: {
  personId: string; account: WorkerLeaveAccount | null; visible: boolean; onHide: () => void;
}) {
  const [date, setDate] = useState('');
  const [ordinaryHours, setOrdinaryHours] = useState<number | null>(null);
  const [forecast, setForecast] = useState<WorkerLeaveForecast | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const requestVersion = useRef(0);
  const hourlySupported = account?.forecastHourlySupported ??
    (account?.leaveTypeCode === 'annual' && account?.accrualBasis === 'progressive_worked_hours/hours');
  const isPersonal = account?.leaveTypeCode === 'personal';
  useEffect(() => {
    requestVersion.current += 1;
    setDate(''); setOrdinaryHours(null); setForecast(null); setError(null); setBusy(false);
  }, [personId, account?.id, account?.rowVersion, account?.accountRowVersion,
    account?.ruleRowVersion, account?.currentBalanceHours, account?.updatedAt, visible]);

  async function calculate() {
    if (!account || !hourlySupported) return;
    setForecast(null);
    if (ordinaryHours === null) { setError('Enter expected contributing ordinary hours.'); return; }
    if (!date) { setError('Enter a forecast date.'); return; }
    if (ordinaryHours < 0 || ordinaryHours > 10000) { setError('Expected hours must be between 0 and 10000.'); return; }
    setError(null); setBusy(true);
    const currentRequest = ++requestVersion.current;
    const result = await forecastWorkerLeave(personId, account.id, {
      forecastToDate: date, expectedContributingOrdinaryHours: ordinaryHours,
      expectedAccountRowVersion: account.accountRowVersion, expectedRuleRowVersion: account.ruleRowVersion,
      expectedAssignmentRowVersion: account.rowVersion,
    });
    if (currentRequest !== requestVersion.current) return;
    setBusy(false);
    if (!result.ok || !result.data) { setError(result.message ?? 'Forecast unavailable. Reload the Leave account and retry.'); return; }
    setForecast(result.data);
  }

  return <Modal visible={visible} header={`Forecast ${account?.leaveTypeName ?? 'Leave'}`} size="large" onHide={onHide}>
    <div className="mgo-stack mgo-gap-l">
      {hourlySupported && <Callout tone="caution" title="Estimate using your assumptions">
        Projection starts from the currently stored balance. Its accrual coverage and balance-as-of are unknown. Enter the ordinary hours you expect to contribute; future leave consumption is unknown and excluded.
        {isPersonal && ' This estimate does not verify Work Assignment entitlement, employment basis or which entered hours qualify as contributing hours.'}
      </Callout>}
      {account && <div className="mgo-stack mgo-gap-s" aria-label="Selected Leave account">
        <DetailPair label="Leave Type" value={account.leaveTypeName} />
        <DetailPair label="Linked Leave Rule" value={`${account.ruleName} (${account.ruleCode})`} />
        <DetailPair label="Rule version" value={String(account.ruleVersion)} />
        <DetailPair label="Stored balance (hours)" value={displayHours(account.currentBalanceHours)} />
      </div>}
      {!hourlySupported && <Callout tone="caution" title="Calculation unavailable">
        This Leave calculation method is not supported by this forecast.
      </Callout>}
      {hourlySupported && <><p>The forecast interval starts after the Business Record date and includes the selected forecast date. Choose a date within 12 calendar months. The Business Record date is confirmed when you calculate.</p>
      <DateField label="Forecast to" dateFormat="ymd" value={date} required fullWidth
        onChange={(value) => { requestVersion.current += 1; setBusy(false); setDate(value); setForecast(null); }}
        onClear={() => { requestVersion.current += 1; setBusy(false); setDate(''); setForecast(null); }} />
      <NumberField label="Expected contributing ordinary hours" value={ordinaryHours} required min={0} max={10000}
        precision={4} allowNegative={false} helpText="Enter your own assumption for the forecast interval; this is not derived from future rosters or payroll."
        onChange={(value) => { requestVersion.current += 1; setBusy(false); setOrdinaryHours(value); setForecast(null); }} />
      {error && <div role="alert"><Callout tone="critical" title="Forecast unavailable">{error}</Callout></div>}
      <div className="mgo-cluster mgo-gap-s mgo-justify-end">
        <Button label="Cancel" variant="tertiary" onClick={onHide} />
        <Button label="Calculate Forecast" variant="primary" loading={busy} disabled={busy} onClick={() => void calculate()} />
      </div>
      </>}
      {forecast && <div className="mgo-stack mgo-gap-s" aria-live="polite">
        {forecast.status === 'calculated' ? <>
          <strong>{`Estimated ${forecast.leaveTypeName}`}</strong>
          <DetailPair label="Forecast interval" value={`${forecast.fromDate} to ${forecast.forecastToDate}`} />
          <DetailPair label="Starting stored balance (hours)" value={displayHours(forecast.startingStoredBalanceHours)} />
          <DetailPair label="Account snapshot updated at" value={forecast.accountUpdatedAt} />
          <DetailPair label="Expected contributing ordinary hours (your assumption)" value={displayHours(forecast.expectedContributingOrdinaryHours)} />
          {forecast.leaveTypeCode === 'personal'
            ? <DetailPair label="Projected Personal accrual (hours)" value={displayHours(forecast.projectedPersonalAccrualHours ?? null)} />
            : <DetailPair label="Projected Annual accrual (hours)" value={displayHours(forecast.projectedAnnualAccrualHours)} />}
          <DetailPair label="Estimated closing balance (hours)" value={displayHours(forecast.estimatedClosingBalanceHours)} />
          <DetailPair label="Future leave consumption" value="Unknown; excluded" />
          <DetailPair label="Accrual rate (hours per contributing hour)" value={forecast.accrualRate.toFixed(6)} />
          <DetailPair label="Rule" value={`${forecast.ruleCode} version ${forecast.ruleVersion}`} />
        </> : <Callout tone="caution" title="Calculation unavailable">{forecast.issues.map(issueText).join(' ')}</Callout>}
        {forecast.warnings.map((warning) => <p key={warning}>{warning}</p>)}
      </div>}
    </div>
  </Modal>;
}
